import React, { memo, useMemo } from 'react';
import PeriodSelector, {
    PeriodSelectorUICallbackState,
    PeriodType,
    PERIOD_SELECTOR_TEXTS,
    SelectablePeriodTemplateState,
} from 'adapter/in/common/components/PeriodSelector';
import { StyledSelectCell, StyledWidgetSelector } from 'adapter/in/common/components/Selector/style';
import { ConfigFieldType, FieldStatus, PeriodSelectorPeriods, SelectorFieldDataImpl, SelectorType } from 'adapter/in/common/form/types';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { endOfDay, format, startOfDay } from 'date-fns';
import { Row, Cell } from '@dexma/ui-components';

type FormPeriodSelectorProps = {
    label: string;
    primaryColor: string;
    onChange: (calendar: PeriodSelectorUICallbackState) => void;
    disabled?: boolean;
    value: string | undefined;
    options: SelectablePeriodTemplateState[];
    dateFormat: string;
    style?: React.CSSProperties;
    hasContext?: boolean;
};

export const FormPeriodSelector = memo(
    ({ label, primaryColor, onChange, disabled, value, options, dateFormat, style, hasContext }: FormPeriodSelectorProps) => {
        const defaultValue = useMemo(() => (value !== undefined ? parsePeriodCodeToPeriodSelectorState(value) : undefined), [value]);

        return (
            <StyledWidgetSelector $error={false} style={style}>
                <>
                    <Row>
                        <Cell className='widget-type-block'>
                            <span>{label}</span>
                        </Cell>
                    </Row>
                    <Row>
                        <StyledSelectCell>
                            <PeriodSelector
                                primaryColor={primaryColor}
                                onChange={onChange}
                                disabled={disabled}
                                defaultValue={defaultValue}
                                texts={PERIOD_SELECTOR_TEXTS}
                                templatePeriods={options}
                                dateFormat={dateFormat}
                                referenceDate={new Date()}
                                hasContext={hasContext}
                            />
                        </StyledSelectCell>
                    </Row>
                </>
            </StyledWidgetSelector>
        );
    }
);

export const onChangePeriodSelector = (onChange: (value: SelectorFieldDataImpl) => void, configType: ConfigFieldType) => (
    calendar: PeriodSelectorUICallbackState
) => {
    const periodCode = parsePeriodSelectorStateToPeriodCode(calendar);
    onChange(
        buildSelectorFieldData(FieldStatus.HAS_VALUE, periodCode, configType, [
            {
                type: SelectorType.PERIOD,
                value: periodCode,
                label: periodCode,
                color: periodCode,
                id: periodCode,
            },
        ])
    );
};

export const parsePeriodCodeToPeriodSelectorState = (code: string): PeriodSelectorUICallbackState => {
    const splitCode = code.split('_');
    if (code === PeriodType.CONTEXT) {
        return { type: PeriodType.CONTEXT };
    }
    const type = splitCode[0] === 'FIXED' ? PeriodType.FIXED : PeriodType.ROLLING;
    if (type === PeriodType.ROLLING) {
        return {
            type,
            isWithCurrent: splitCode[0] === 'CURRENT',
            number: parseFloat(splitCode[1]),
            period: splitCode[2] as PeriodSelectorPeriods,
            isToEnd: splitCode[3] === 'TO-END',
        };
    }
    return {
        type: PeriodType.FIXED,
        isToEnd: splitCode[3] === 'TO-END',
        startDate: new Date(splitCode[1]),
        endDate: new Date(splitCode[2]),
    };
};

export const parsePeriodSelectorStateToPeriodCode = (state: PeriodSelectorUICallbackState): string => {
    if (state.type === PeriodType.FIXED) {
        return `${state.type}_${format(startOfDay(state.startDate), "yyyy-MM-dd'T'HH:mm:ss")}_${format(
            endOfDay(state.endDate),
            "yyyy-MM-dd'T'HH:mm:ss"
        )}_${state.isToEnd ? 'TO-END' : 'TO-NOW'}`;
    }
    if (state.type === PeriodType.ROLLING) {
        return `${state.isWithCurrent ? 'CURRENT' : 'PREVIOUS'}_${state.number}_${state.period}_${state.isToEnd ? 'TO-END' : 'TO-NOW'}`;
    }
    if (state.type === PeriodType.CONTEXT) {
        return PeriodType.CONTEXT;
    }
    return 'error';
};
